import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Package,
  Database,
  Code,
  Shield,
  Brain,
  Cloud,
  GraduationCap,
  VirtualReality,
  VideoCamera,
  ArrowUpRight,
} from "@phosphor-icons/react";
import SectionHeading from "@/components/SectionHeading";
import { SERVICES } from "@/data/content";

const ICONS = {
  package: Package,
  database: Database,
  code: Code,
  shield: Shield,
  brain: Brain,
  cloud: Cloud,
  graduation: GraduationCap,
  vr: VirtualReality,
  camera: VideoCamera,
};

const PROCESS = [
  { n: "01", t: "Discovery", d: "Deep-dive workshops to understand business goals, constraints and risks." },
  { n: "02", t: "Architecture", d: "Solution design, security threat models, sprint plan and clear acceptance criteria." },
  { n: "03", t: "Build", d: "Agile delivery with weekly demos, security reviews on every PR, full test coverage." },
  { n: "04", t: "Operate", d: "Hardened deployment, observability, training and 24×7 support retainers." },
];

export default function Services() {
  return (
    <div data-testid="services-page" className="bg-white">
      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 border-b border-slate-200">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="overline mb-5">Our services</div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.02] font-black text-slate-900">
              End-to-end engineering, <span className="text-[#1D4ED8]">from threat model to production.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-base md:text-lg text-slate-600 leading-relaxed font-light">
              Six service lines, one delivery model. We architect, build, secure and operate the systems that run your business.
            </p>
          </div>
        </div>
      </section>

      {/* Service grid */}
      <section className="py-20 md:py-32 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || Code;
            return (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.07 }}
              >
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-link-${s.slug}`}
                  className="card-flat p-7 md:p-9 group h-full flex flex-col justify-between min-h-[280px]"
                >
                  <div>
                    <div className="flex items-center justify-between">
                      <Icon size={32} weight="duotone" className="text-[#1D4ED8]" />
                      <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500">
                        0{i + 1}
                      </span>
                    </div>
                    <h3 className="mt-6 font-display text-xl md:text-2xl font-semibold text-slate-900 tracking-tight">
                      {s.name}
                    </h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed font-light">
                      {s.short}
                    </p>
                  </div>
                  <div className="mt-6 flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-600 group-hover:text-[#1D4ED8] transition-colors">
                    Read more <ArrowUpRight size={14} weight="bold" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Process */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading
            eyebrow="Engagement model"
            title="Predictable delivery, measurable outcomes."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-5">
            {PROCESS.map((p) => (
              <div key={p.n} className="card-flat p-7 md:p-8">
                <div className="font-display text-4xl md:text-5xl font-black text-[#1D4ED8] tracking-tighter">
                  {p.n}
                </div>
                <h4 className="mt-4 font-display text-lg font-semibold text-slate-900 tracking-tight">{p.t}</h4>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed font-light">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
